import React, { Component } from 'react'
import { render } from 'react-dom'

import { InlineMath, BlockMath } from 'react-katex';

import {
  Header,
} from 'semantic-ui-react'

import 'katex/dist/katex.min.css';
import '../../App.css'

class Chapter106 extends Component {
  render() {
    return (
      <div>

        <Header as='h1'>
        1-6. デリシュレーNBDモデル
        </Header>

        <Header as='h2'>
        デリシュレーNBDモデルとは
        </Header>

　デリシュレーNBDモデルとは、あるカテゴリーの中のブランド間の関係を教えてくれる確率分布になります。この分布を用いてわかる具体例として本書で上げられているのが、P31の表1-4になります。与えら得た式(後述の式１)に対して、どのようにパラメタを計算しているのかが具体的に示されています。 <br/>

　本章の内容はかなり高度なレベルとなっています。少しずつ読み解いてみます。

        <Header as='h2'>
        概要
        </Header>

　まず結論からです。デリシュレーNBDは以下の様な数式で表せると結論づけています。 <br/>



<BlockMath>{`
   
\\begin{aligned}
P_{r} \\left(R,r_1, r_2, \\cdots , r_g \\right) &=\\frac{\\Gamma\\left(S \\right)}{\\displaystyle \\prod_{i=1}^{N} \\Gamma\\left(\\alpha_j \\right)} \\cdot \\frac{\\displaystyle \\prod_{i=1}^{N} \\Gamma\\left(r_j + \\alpha_j \\right)}{\\Gamma\\left(S+R \\right)} \\cdot \\frac{1}{\\displaystyle \\prod_{j=1}^{g} r_j!} \\cdot \\\\\\\\
&\\qquad \\frac{\\Gamma\\left(R + K \\right)}{\\displaystyle \\Gamma\\left(K \\right)} \\cdot \\left(1 + \\frac{K}{MT} \\right)^{-R} \\cdot\\left(1 + \\frac{MT}{K} \\right)^{-K} \\,\\,\\,\\,\\cdot\\cdot\\cdot\\cdot\\,\\,\\,\\,\\left(1\\right) \\\\\\\\
R&=\\sum_{j=i}^{g}r_j  \\,\\,\\,\\,\\cdot\\cdot\\cdot\\cdot\\,\\,\\,\\,\\left(2\\right)  \\\\\\\\
\\alpha_j &=S \\times\\left( \\text{ブランドjの購入頻度に基づくマーケットシェア} \\right) \\,\\,\\,\\,\\cdot\\cdot\\cdot\\cdot\\,\\,\\,\\,\\left(3\\right)  \\\\\\\\
S &=\\sum_{j=1}^g \\alpha_j \\,\\,\\,\\,\\cdot\\cdot\\cdot\\cdot\\,\\,\\,\\,\\left(4\\right) 
\\end{aligned}

`}</BlockMath>
      

　そして、数式が成り立つ前提は以下の様になっています。 <br/>

<ol className="ol_01">
  <li className="li_01">消費者各自の購買行動は独立事象</li>
  <li className="li_01">購入行動はランダムに発生</li>
  <li className="li_01">
      購入者各自<InlineMath>{`\\left(C_i \\right)`}</InlineMath>は、一定のカテゴリーに対して、長期的購入回数の平均値<InlineMath>{`\\mu_i`}</InlineMath>を持つ。購入者各自<InlineMath>{`\\left(C_i \\right)`}</InlineMath>の単位時間のカテゴリ購入回数R_iはポアソン分布している。

<BlockMath>{`
  R_i \\sim Poisson\\left(\\mu_i \\right)
`}</BlockMath>
  
      </li>

  <li className="li_01">カテゴリーの長期平均購入回数<InlineMath>{`\\left(\\mu \\right)`}</InlineMath>は消費者間で異なり、ガンマ分布している。

<BlockMath>{`
  \\displaystyle \\mu \\sim Gamma\\left(K,\\frac{M}{K} \\right) 
`}</BlockMath>

      </li>

  <li className="li_01">期間Tにおける各ブランドの購入回数<InlineMath>{`\\left(r_j \\right)`}</InlineMath>は、ガンマ分布<InlineMath>{`Gamma\\left(\\alpha_j,\\beta \\right)`}</InlineMath>に従う。<InlineMath>{`\\alpha`}</InlineMath>はブランド間で異なるが、<InlineMath>{`\\beta`}</InlineMath>はブランド間で同一。</li>

  <li className="li_01">各消費者は、各ブランドに対して一定の購買確率を持っており、ブランドの購入<InlineMath>{`\\left(r \\right)`}</InlineMath> は多項分布に従う。各々のカテゴリーの購入時のブランドの購入確率<InlineMath>{`\\left(p \\right)`}</InlineMath>は、それぞれのブランドについて長期的に見ると決まっている。ただ、カテゴリー購入時にどれを選ぶかはランダムである。</li>

  <li className="li_01">異なる人々の各々のカテゴリー平均購入回数と、人々のそれぞれのブランドを選択する確率とは、互いに独立。すなわち、特定のカテゴリー購入回数の人が、特定のブランドを特定の確率で購入しているような事が起こらない。</li>

</ol>

        <Header as='h2'>
        パート1
        </Header>

　ポアソン分布とガンマ分布の混合分布から負の二項分布を導きます。 <br/>

<BlockMath>{`
\\begin{aligned}
P\\left(r \\right) &=\\int_0^{\\infty} Po\\left(r|\\lambda \\right) Ga\\left(\\lambda | \\alpha, \\theta \\right)  d\\lambda    \\\\\\\\
&= \\int_0^{\\infty} \\frac{\\lambda^r e^{-\\lambda}}{r!}\\cdot \\frac{\\lambda^{\\alpha - 1}e^{-\\frac{\\lambda}{\\theta}}}{\\Gamma\\left(\\alpha \\right)\\theta^\\alpha } d\\lambda \\\\\\\\
&= \\frac{1}{r!\\Gamma\\left(\\alpha \\right)\\theta^\\alpha}  \\int_0^{\\infty} \\lambda^{r+\\alpha-1}e^{-\\lambda\\left(1 +\\frac{1}{\\theta}  \\right)} d\\lambda 
\\end{aligned}

`}</BlockMath>


　ここで、ガンマ関数が、

<BlockMath>{`
  \\Gamma\\left(r \\right) = \\int_0^{\\infty}t^{r-1}e^{-t}dt
`}</BlockMath>

と定義できる事を念頭に、

<BlockMath>{`
  \\lambda \\left(1 +\\frac{1}{\\theta}  \\right)  = t \\rightarrow \\lambda =  \\frac{\\theta t}{1 + \\theta } 
`}</BlockMath>

と変数変換すると、


<BlockMath>{`
\\begin{aligned}
P\\left(r \\right) &= \\frac{1}{r!\\Gamma\\left(\\alpha \\right)\\theta^\\alpha}  \\int_0^{\\infty} \\lambda^{r+\\alpha-1}e^{-\\lambda\\left(1 +\\frac{1}{\\theta}  \\right)} d\\lambda \\\\\\\\
&=\\frac{1}{r!\\Gamma\\left(\\alpha \\right)\\theta^\\alpha}  \\int_0^{\\infty}\\left(\\frac{\\theta t}{1 + \\theta} \\right)^{r+\\alpha -1}e^{-t}\\frac{\\theta}{1 + \\theta}dt \\\\\\\\
&=\\frac{1}{r!\\Gamma\\left(\\alpha \\right)\\theta^\\alpha} \\left(\\frac{\\theta}{1 + \\theta} \\right)^{r+\\alpha}\\int_0^{\\infty}t^{r+\\alpha-1}e^{-t}dt \\\\\\\\
&= \\frac{1}{r!\\Gamma\\left(\\alpha \\right)\\theta^\\alpha} \\left(\\frac{\\theta}{1 + \\theta} \\right)^{r+\\alpha}\\Gamma\\left(r + \\alpha \\right) 
\\end{aligned}

`}</BlockMath>

となります。また、<InlineMath>{`r! = \\Gamma\\left(r +1  \\right)`}</InlineMath> に注意すると、



<BlockMath>{`
\\begin{aligned}
P\\left(r \\right) &= \\frac{\\Gamma\\left(r+\\alpha \\right)}{\\Gamma\\left(\\alpha \\right)\\Gamma\\left(r + 1 \\right)\\theta^\\alpha} \\left(\\frac{\\theta}{1 + \\theta} \\right)^{r+\\alpha} \\\\\\\\
&= \\frac{\\Gamma\\left(r+\\alpha \\right)}{\\Gamma\\left(\\alpha \\right)\\Gamma\\left(r + 1 \\right)} \\left(\\frac{\\theta}{1 + \\theta} \\right)^{r} \\left(1 + \\theta \\right)^{-\\alpha} \\\\\\\\
&= \\frac{\\left(1 + \\theta \\right)^{-\\alpha} \\cdot \\Gamma\\left(r+\\alpha \\right)}{\\Gamma\\left(\\alpha \\right)\\Gamma\\left(r + 1 \\right)} \\left(\\frac{\\theta}{1 + \\theta} \\right)^{r} 
\\end{aligned}
`}</BlockMath>

と変形できます。ここで、<InlineMath>{`\\alpha = K`}</InlineMath>、<InlineMath>{`\\displaystyle \\theta = \\frac{M}{K}`}</InlineMath> を代入すると、


<BlockMath>{`
P\\left(r \\right) = \\frac{\\left(1 + \\frac{M}{K} \\right)^{-K} \\cdot \\Gamma\\left(K + r \\right)}{\\Gamma\\left(r + 1 \\right)\\cdot \\Gamma\\left(K \\right)} \\cdot \\left(\\frac{M}{M+K} \\right)^r 
`}</BlockMath>

となり、1-3の負の二項分布の章で示した以下の式と一致します。以上から、

<div className="box2">
  <p>消費者個人の購買活動がポアソン分布しており、その長期平均購入回数がガンマ分布していることを仮定することによって、ある期間における消費者全体の購入回数は負の二項分布している</p>
</div>


という事が導けました。


        <Header as='h2'>
        パート2
        </Header>

編集中 <br/>

      </div>
    )   
  }
}

export default Chapter106;
